// Pull a printable exam paper out of a generated rubric.
// Keeps section headings. When every question under a heading is worth the same marks,
// the heading gains "each question carries N marks" and each question drops its own "(N marks)".
// A heading that already says "N marks each" is left as written.
// Drops marking guidelines, deduction rules, model answers, sample solutions, and rubric tables.
// Keeps a passage, poem, extract, or paragraph that the question tells the student to read.
// A digit the model answer names stays underlined on the question.

import { paperUnderlineMap, splitUnderlinedDigits } from './paperDiff';
import { fractionHtml } from './questionFractions';
import { isDataLine, segmentLines } from './dataTable';
import { parseChoiceParent } from './rubricQuestions';

// The words a student must read (اقتباس، شعر، passage, poem) live in an
// HTML comment. The question paper reveals it. The rubric view, the copy,
// and the marking guide strip it, so it never appears as part of the rubric.
export function stripQuestionPaperText(markdown) {
  return String(markdown || '')
    .replace(/<!--\s*qp\b[\s\S]*?-->/gi, '')
    .replace(/\n{3,}/g, '\n\n');
}

function revealQuestionPaperText(markdown) {
  return String(markdown || '').replace(/<!--\s*qp\b([\s\S]*?)-->/gi, (_, body) => {
    const text = String(body || '').replace(/^\s*\n/, '').replace(/\s*$/, '');
    return text ? `\n${text}\n` : '\n';
  });
}

function questionLabelPattern(label) {
  return `${String(label).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![0-9A-Za-z.])`;
}

// Put hidden paper notes back after a teacher edits the visible rubric.
export function restorePaperNotes(edited, original) {
  const source = String(original || '');
  const notes = [];
  const re = /<!--\s*qp\b[\s\S]*?-->/gi;
  let found;
  while ((found = re.exec(source))) notes.push({ raw: found[0], index: found.index });
  if (!notes.length) return String(edited || '');
  let out = String(edited || '');
  notes.forEach((note) => {
    if (out.includes(note.raw)) return;
    const before = source.slice(0, note.index);
    const headers = [...before.matchAll(/^Q\.?\s*[0-9]+(?:\.[A-Za-z0-9]+)*\s*[-–—:]/gim)];
    const last = headers.length ? headers[headers.length - 1] : null;
    const between = last ? before.slice(last.index) : '';
    const afterAnswer = !!(last && /^(?:model\s+answer|expected\s+answer|sample\s+(?:answer|solution)|solution|جواب|نمونہ\s*جواب)\b/im.test(between));
    if (last && !afterAnswer) {
      const label = last[0].match(/Q\.?\s*[0-9]+(?:\.[A-Za-z0-9]+)*/i)[0];
      const qRe = new RegExp(`(^[ \\t]*${questionLabelPattern(label)}\\s*[-–—:][^\\n]*\\n[ \\t]*={3,}[ \\t]*\\n)`, 'im');
      if (qRe.test(out)) out = out.replace(qRe, `$1${note.raw}\n`);
      return;
    }
    const after = source.slice(note.index + note.raw.length);
    const next = after.match(/Q\.?\s*[0-9]+(?:\.[A-Za-z0-9]+)*\s*[-–—:]/i);
    if (!next) return;
    const label = next[0].match(/Q\.?\s*[0-9]+(?:\.[A-Za-z0-9]+)*/i)[0];
    const qRe = new RegExp(`(^[ \\t]*${questionLabelPattern(label)}\\s*[-–—:])`, 'im');
    if (qRe.test(out)) out = out.replace(qRe, `${note.raw}\n\n$1`);
  });
  return out;
}

const QUESTION_LINE_RE = /^Q\.?\s*[0-9]+(?:\.[A-Za-z0-9]+)*\s*[-–—:]/i;
const TOTAL_MARKS_RE = /^total\s+marks\s*[:：]\s*(.+)$/i;

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';
const ELEMENT_SYMBOLS = new Set('H He Li Be B C N O F Ne Na Mg Al Si P S Cl Ar K Ca Sc Ti V Cr Mn Fe Co Ni Cu Zn Ga Ge As Se Br Kr Rb Sr Y Zr Nb Mo Tc Ru Rh Pd Ag Cd In Sn Sb Te I Xe Cs Ba La Ce Pr Nd Pm Sm Eu Gd Tb Dy Ho Er Tm Yb Lu Hf Ta W Re Os Ir Pt Au Hg Tl Pb Bi Po At Rn Fr Ra Ac Th Pa U'.split(' '));

function toSubscriptDigits(digits) {
  return [...digits].map((digit) => SUBSCRIPT_DIGITS[digit]).join('');
}

function elementAt(text, index) {
  const two = text.slice(index, index + 2);
  if (two.length === 2 && ELEMENT_SYMBOLS.has(two) && /[a-z]/.test(two[1])) return true;
  const one = text.slice(index, index + 1);
  if (!ELEMENT_SYMBOLS.has(one)) return false;
  const next = text.slice(index + 1, index + 2);
  return !next || !/[a-z]/.test(next);
}

// H2O, CH4, NH4OH, H2SO4, Ca(HCO3)2, and (NH4)2SO4 print with formula subscripts.
// A coefficient stays on the line: 2H2O is 2H₂O. "(5 marks)" stays as written.
function subscriptFormulas(text) {
  let s = text.replace(/([A-Z][a-z]?)(\d+)/g, (match, symbol, digits) => (
    ELEMENT_SYMBOLS.has(symbol) ? symbol + toSubscriptDigits(digits) : match
  ));
  s = s.replace(/\)(\d+)/g, (match, digits, offset, whole) => {
    const after = whole.slice(offset + match.length).replace(/^\s+/, '');
    if (!after || /^[+\-=]/.test(after) || /^(?:→|->|=>|⇌|↔)/.test(after) || elementAt(after, 0)) {
      return `)${toSubscriptDigits(digits)}`;
    }
    return match;
  });
  return s;
}

function cleanLine(raw) {
  let s = String(raw || '').trim();
  s = s.replace(/^#{1,6}\s+/, '');
  s = s.replace(/\*\*/g, '').replace(/__/g, '').replace(/`/g, '').replace(/\$/g, '');
  s = s.replace(/_(\d+)/g, (_, digits) => toSubscriptDigits(digits));
  s = subscriptFormulas(s);
  return s.trim();
}

function isQuestionLine(line) {
  return QUESTION_LINE_RE.test(line);
}

function isBareQuestionLabel(line) {
  return /^Q\.?\s*[0-9]+(?:\.[A-Za-z0-9]+)*\s*$/i.test(line);
}

function isPipeRow(line) {
  return line.startsWith('|') && (line.match(/\|/g) || []).length >= 2;
}

function isRubricTableLead(line) {
  return /^(rubrics?\s+breakdown|grading(\s+criteria)?|marks?\s+breakdown)\b/i.test(line);
}

function isMarksTableHeader(line) {
  return isPipeRow(line) && (/\b(sub-?steps?|marks?)\b/i.test(line) || /نمبر/.test(line));
}

function looksLikeSectionHeading(line) {
  if (/\b(section|questions?|answers?)\b/i.test(line)) return true;
  if (/\bmarks?\b/i.test(line)) return true;
  if (/حصہ/.test(line)) return true;
  return false;
}

// The line that introduces a shared reading: "read the following paragraph
// and answer the questions below." It belongs above the reading, not inside it.
function isParentInstruction(line) {
  if (/درج\s*ذیل|مندرجہ\s*ذیل|دیے\s*گئے\s*سوالات|پڑھ\s*کر/.test(line)) return true;
  if (/\b(read|study)\s+the\s+following\b/i.test(line)) return true;
  if (/\banswer the (?:questions|following)\b/i.test(line)) return true;
  if (/\bfollowing (?:passage|paragraph|poem|extract|stanza|text)\b/i.test(line)) return true;
  return false;
}

// Worked solution text that must never become a question or a section heading.
// A division grid ("2 | 10, 20, 30", "| 1, 1, 1") is not a markdown table:
// a real marks row has at least two pipes ("| Sub-step | Marks |").
// A reaction the student is given to finish: "Ca(HCO₃)₂ + HCl → …" or "Mg + O₂ →".
// A worked sum such as "LCM = 2 × 2 × 3 = 12" is not one of these.
function isChemicalEquation(line) {
  const bare = String(line).replace(/^(?:\d+[.)]\s+|[-•*]+\s+)*/, '').trim();
  if (!bare || !/[A-Za-z]/.test(bare)) return false;
  if (/→|->|=>|⇌|↔/.test(bare)) return true;
  return /[A-Z][a-z]?(?:\d+|_\d+|[₀-₉]+)/.test(bare) && /=/.test(bare);
}

function isSolutionLine(line) {
  const bare = String(line).replace(/^(?:[-•*]+\s+)+/, '').trim();
  if (!bare) return false;
  if (/=/.test(bare) && /[0-9]/.test(bare)) return true;
  if (looksLikeSectionHeading(bare)) return false;
  if (/\bany\s+valid\b/i.test(bare)) return true;
  if (/\bacceptable\b/i.test(bare)) return true;
  if (/\b(or\s+equivalent|accept\s+any)\b/i.test(bare)) return true;
  if (/^[0-9]+\s*\|/.test(bare)) return true;
  if (/^\|\s*[0-9]/.test(bare) && (bare.match(/\|/g) || []).length < 2) return true;
  return false;
}

function isSeparator(line) {
  return /^[=\-_.*]{3,}$/.test(line);
}

function isModelAnswerLead(line) {
  return /^(model\s+answer|expected\s+answer|sample\s+(answer|solution)|solution)\b/i.test(line)
    || /^جواب(?:\s*[:：]|\s*$)/.test(line)
    || /^نمونہ\s*جواب/.test(line);
}

function isBodyStart(line) {
  if (!line) return false;
  if (/^={3,}$/.test(line)) return true;
  if (line.startsWith('|')) return true;
  if (isSolutionLine(line)) return true;
  if (/^(model\s+answer|expected\s+answer|sample\s+(answer|solution)|solution|rubrics?\s+breakdown|grading(\s+criteria)?|marks?\s+breakdown)\b/i.test(line)) {
    return true;
  }
  if (/^جواب\s*[:：]/.test(line)) return true;
  if (/^نمونہ\s*جواب/.test(line)) return true;
  return false;
}

// A grader rule, not a question. "-0.1 spelling mistake", "-0.1 for spelling
// mistake", and "-0.1 Question number incorrect (QN.INC): ..." are this.
// A leading minus with a decimal (or 1) is the deduction amount. "-2" is not,
// so a given value on the paper is kept.
function isDeductionLine(line) {
  const bare = String(line || '').replace(/^(?:[-•*]+\s+)+/, '').trim();
  if (!bare) return false;
  if (/^marking\s+guidelines\b/i.test(bare)) return true;
  if (/\bdeduction\s+rules\b/i.test(bare)) return true;
  if (/^-?[0-9]+(?:\.[0-9]+)?\s+for\b/i.test(bare)) return true;
  if (/^-(?:0\.[0-9]+|1(?:\.[0-9]+)?)\s+\S/.test(bare) && /\b(?:mistake|error|incorrect|grammar|spelling|deduct|penalty|unit|notation|wording)\b/i.test(bare)) return true;
  if (/\bQN\.INC\b/.test(bare)) return true;
  if (/question\s+number\s+incorrect/i.test(bare)) return true;
  if (/out-of-order answering/i.test(bare)) return true;
  if (/matched rubric row/i.test(bare)) return true;
  if (/کٹوتی/.test(bare)) return true;
  if (/غلطی\s*پر/.test(bare) && /[0-9]/.test(bare)) return true;
  return false;
}

// The sentence after a deduction amount, when the rule wraps onto the next line.
// "Part- 1 Short Questions:" is a heading, so the wrap stops there.
// Before the first question, any "-0.1 ..." line is a deduction rule.
// Inside a question the same shape can be a given value, so it stays.
function isPreambleDeduction(line) {
  const bare = String(line || '').replace(/^(?:[-•*]+\s+)+/, '').trim();
  return /^-(?:0\.[0-9]+|1(?:\.[0-9]+)?)\s+\S/.test(bare);
}

function isDeductionContinuation(line) {
  if (!line) return false;
  if (parseChoiceParent(line)) return false;
  if (isQuestionLine(line) || isBareQuestionLabel(line)) return false;
  if (isParentInstruction(line)) return false;
  if (/^(?:part|section|short|long|mcq|question)\b/i.test(line)) return false;
  if (/^حصہ/.test(line)) return false;
  if (looksLikeSectionHeading(line) && line.length < 80 && !/\bQN\.INC\b|deduct|mistake/i.test(line)) return false;
  return true;
}

function isNoise(line) {
  if (!line) return true;
  if (isBodyStart(line)) return true;
  if (/^[=\-_.*]{3,}$/.test(line)) return true;
  if (isDeductionLine(line)) return true;
  if (/^sub-?steps?\b/i.test(line) && /marks?\b/i.test(line)) return true;
  if (isSolutionLine(line)) return true;
  return false;
}

// Lines before "Model Answer:" / "جواب" are the source text the student must
// read (اقتباس، شعر، پیراگراف، passage, poem). The answer itself stays off
// the paper. A section heading is kept only once the marks table has finished,
// so a "|" inside a division working cannot promote the result onto the paper.
function consumeUntilNextQuestion(lines, startIndex, blocks, paper, question) {
  let i = startIndex;
  let tableStarted = false;
  let tableFinished = false;
  let inAnswer = false;
  let collectingWords = false;
  const stimulus = [];
  const trailing = [];
  const underlineWords = [];

  const flushTrailing = () => {
    if (!trailing.length) return;
    blocks.push({ type: 'passage', text: trailing.join('\n') });
    trailing.length = 0;
  };

  while (i < lines.length) {
    const body = cleanLine(lines[i]);
    if (body && (isQuestionLine(body) || isBareQuestionLabel(body))) break;
    if (body && parseChoiceParent(body)) {
      blocks.push({ type: 'section', text: body });
      i += 1;
      continue;
    }
    if (body && !isSeparator(body)) {
      if (!inAnswer && !tableStarted && isModelAnswerLead(body)) {
        inAnswer = true;
      } else if (isPipeRow(body)) {
        if (!tableFinished && (tableStarted || isMarksTableHeader(body))) {
          tableStarted = true;
          inAnswer = true;
        } else if (!inAnswer && !tableStarted) {
          stimulus.push(body);
        } else if (inAnswer && !tableStarted) {
          collectUnderlinedWord(body, underlineWords, () => { collectingWords = true; }, () => { collectingWords = false; }, collectingWords);
        }
      } else if (!tableFinished && isRubricTableLead(body)) {
        tableStarted = true;
        inAnswer = true;
      } else if (!inAnswer && !tableStarted) {
        if (!isNoise(body) || isChemicalEquation(body)) stimulus.push(body);
      } else if (inAnswer && !tableStarted) {
        collectUnderlinedWord(body, underlineWords, () => { collectingWords = true; }, () => { collectingWords = false; }, collectingWords);
      } else if (tableStarted) {
        tableFinished = true;
        if (!isNoise(body) && !isChemicalEquation(body) && !takeTotal(body, paper)) {
          if (looksLikeSectionHeading(body) || isParentInstruction(body) || parseChoiceParent(body)) {
            flushTrailing();
            blocks.push({ type: 'section', text: body });
          } else {
            trailing.push(body);
          }
        }
      }
    }
    i += 1;
  }

  if (question && stimulus.length) question.passage = stimulus;
  if (question && underlineWords.length) question.underlineWords = underlineWords;
  flushTrailing();
  return i;
}

function collectUnderlinedWord(line, words, start, stop, collecting) {
  if (/تشریح/.test(line)) {
    stop();
    return;
  }
  if (/خط\s*کشیدہ/.test(line) || /^الفاظ\b/.test(line) || /الفاظ\s+معانی/.test(line)) start();
  if (!collecting && !/خط\s*کشیدہ/.test(line)) return;
  if (isPipeRow(line) && !isMarksTableHeader(line)) {
    const cell = line.split('|').map((part) => part.trim()).filter(Boolean)[0];
    if (cell && !/^(الفاظ|معانی|---)$/.test(cell)) words.push(cell);
    return;
  }
  const columns = line.split(/\s{2,}/).map((part) => part.trim()).filter(Boolean);
  if (columns.length >= 2 && !/^(الفاظ|معانی)$/.test(columns[0]) && !/خط\s*کشیدہ/.test(columns[0])) {
    words.push(columns[0]);
  }
}

function questionParent(label) {
  const parts = String(label || '').split('.');
  if (parts.length < 3) return null;
  const part = parts.pop().toLowerCase();
  return { parent: parts.join('.'), part };
}

function isFirstSubPart(part) {
  return part === 'i' || part === 'a' || part === '1';
}

// A paragraph stored on Q.7.i is the reading for Q.7.ii and Q.7.iii as well.
// Lift it, and any introducing line, to sit once above the whole group.
function hoistSharedPassages(blocks) {
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (block.type !== 'question' || !block.passage || !block.passage.length) continue;
    const info = questionParent(block.label);
    if (!info || !isFirstSubPart(info.part)) continue;
    let siblings = 0;
    let siblingHasPassage = false;
    for (let next = index + 1; next < blocks.length; next += 1) {
      const other = blocks[next];
      if (other.type !== 'question') break;
      const otherInfo = questionParent(other.label);
      if (!otherInfo || otherInfo.parent !== info.parent) break;
      siblings += 1;
      if (other.passage && other.passage.length) siblingHasPassage = true;
    }
    if (!siblings || siblingHasPassage) continue;
    const headings = [];
    const rest = [];
    block.passage.forEach((line) => {
      if (!rest.length && isParentInstruction(line)) headings.push(line);
      else rest.push(line);
    });
    block.passage = [];
    const insert = [];
    headings.forEach((text) => insert.push({ type: 'section', text }));
    if (rest.length) insert.push({ type: 'passage', text: rest.join('\n'), underlineWords: block.underlineWords || [] });
    block.underlineWords = [];
    blocks.splice(index, 0, ...insert);
    index += insert.length;
  }
}

function foldUrdu(text) {
  return String(text || '').replace(/[\u064B-\u065F\u0670\u0640\u200c\u200d]/g, '');
}

function foldMap(text) {
  const folded = [];
  const map = [];
  const source = String(text || '');
  for (let i = 0; i < source.length; i += 1) {
    if (/[\u064B-\u065F\u0670\u0640\u200c\u200d]/.test(source[i])) continue;
    map.push(i);
    folded.push(source[i]);
  }
  return { folded: folded.join(''), map };
}

function markWords(text, words) {
  const source = String(text || '');
  const { folded, map } = foldMap(source);
  const sorted = (words || [])
    .map(foldUrdu)
    .filter((word) => word.length >= 2)
    .sort((a, b) => b.length - a.length);
  const hits = [];
  sorted.forEach((word) => {
    let from = 0;
    while (from <= folded.length - word.length) {
      const at = folded.indexOf(word, from);
      if (at < 0) break;
      const end = at + word.length;
      if (!hits.some((hit) => at < hit.end && end > hit.start)) hits.push({ start: at, end });
      from = at + 1;
    }
  });
  hits.sort((a, b) => a.start - b.start);
  if (!hits.length) return [{ text: source, under: false }];
  const out = [];
  let cursor = 0;
  hits.forEach((hit) => {
    const origStart = map[hit.start];
    const origEnd = map[hit.end - 1] + 1;
    if (origStart > cursor) out.push({ text: source.slice(cursor, origStart), under: false });
    out.push({ text: source.slice(origStart, origEnd), under: true });
    cursor = origEnd;
  });
  if (cursor < source.length) out.push({ text: source.slice(cursor), under: false });
  return out;
}

function markLine(line, words) {
  const raw = String(line || '');
  const tagged = [];
  const re = /<u>([\s\S]*?)<\/u>/gi;
  let last = 0;
  let match;
  while ((match = re.exec(raw))) {
    if (match.index > last) tagged.push({ text: raw.slice(last, match.index), under: false });
    if (match[1]) tagged.push({ text: match[1], under: true });
    last = match.index + match[0].length;
  }
  if (last < raw.length) tagged.push({ text: raw.slice(last), under: false });
  if (!tagged.length) tagged.push({ text: '', under: false });
  const out = [];
  tagged.forEach((part) => {
    if (part.under) out.push(part);
    else out.push(...markWords(part.text, words));
  });
  return out.filter((part) => part.text);
}

function attachPassageUnderlines(block) {
  const words = block.underlineWords || [];
  if (block.type === 'question' && block.passage && block.passage.length) {
    block.passageParts = block.passage.map((line) => markLine(line, words));
  } else if (block.type === 'passage') {
    block.passageParts = String(block.text || '').split('\n').map((line) => markLine(line, words));
  }
}

function sectionStatesMarks(text) {
  return /\bmarks?\b/i.test(text) || /نمبر/.test(text);
}

function sectionSaysEach(text) {
  return /\beach\b/i.test(text) || /ہر\s*سوال/.test(text);
}

function sharedQuestionMarks(questions) {
  if (!questions.length) return '';
  const values = questions.map((question) => String(question.marks || '').trim());
  if (values.some((value) => !value)) return '';
  return values.every((value) => value === values[0]) ? values[0] : '';
}

function carriesPhrase(marks, heading) {
  const rtl = scriptDir(heading) === 'rtl';
  if (rtl) return `ہر سوال کے ${marks} نمبر ہیں`;
  const unit = Number(marks) === 1 ? 'mark' : 'marks';
  return `each question carries ${marks} ${unit}`;
}

function stripQuestionMarks(block) {
  block.rest = String(block.rest || '').replace(/\s*\(\s*[0-9]+(?:\.[0-9]+)?\s*marks?\s*\)\s*$/i, '').trim();
  block.text = String(block.text || '').replace(/\s*\(\s*[0-9]+(?:\.[0-9]+)?\s*marks?\s*\)\s*$/i, '').trim();
  block.marks = '';
}

function takeTotal(line, paper) {
  const m = line.match(TOTAL_MARKS_RE);
  if (!m) return false;
  if (!paper.totalMarks) {
    paper.totalMarks = m[1].replace(/\s*marks?\s*$/i, '').trim();
  }
  return true;
}

function parseQuestionLine(line) {
  const m = line.match(/^(Q\.?\s*[0-9]+(?:\.[A-Za-z0-9]+)*)\s*([-–—:])\s*(.*)$/i);
  const marks = line.match(/\(\s*([0-9]+(?:\.[0-9]+)?)\s*marks?\s*\)\s*$/i);
  return {
    type: 'question',
    label: m ? m[1].trim() : '',
    sep: m ? m[2] : '-',
    rest: m ? m[3].trim() : line,
    text: line,
    marks: marks ? marks[1] : ''
  };
}

function scriptDir(text) {
  const arabic = (String(text).match(/[\u0600-\u06FF]/g) || []).length;
  const latin = (String(text).match(/[A-Za-z]/g) || []).length;
  return arabic > latin ? 'rtl' : 'ltr';
}

function questionId(label) {
  return String(label || '').replace(/^Q\.?\s*/i, '');
}

// The next real question after a choice line is Q.1.i, not another whole question.
function childQuestionFollows(lines, index, parentId) {
  for (let j = index; j < lines.length; j += 1) {
    const body = cleanLine(lines[j]);
    if (!body || isSeparator(body) || isNoise(body) || isModelAnswerLead(body) || isRubricTableLead(body) || isPipeRow(body)) continue;
    if (isQuestionLine(body) || isBareQuestionLabel(body)) {
      const id = (body.match(/Q\.?\s*([0-9]+(?:\.[A-Za-z0-9]+)*)/i) || [])[1] || '';
      return id.startsWith(`${parentId}.`);
    }
    return false;
  }
  return false;
}

/**
 * @returns {{ dir: 'rtl'|'ltr', totalMarks: string, blocks: Array, questionCount: number }}
 */
export function extractQuestionPaper(markdown) {
  const lines = revealQuestionPaperText(markdown).split(/\r?\n/);
  const blocks = [];
  const paper = { totalMarks: '' };
  let i = 0;
  let seenQuestion = false;
  let deductionTail = false;

  while (i < lines.length) {
    const line = cleanLine(lines[i]);
    if (!line) {
      deductionTail = false;
      i += 1;
      continue;
    }

    const choice = parseChoiceParent(line);
    if (choice && (!isQuestionLine(line) || childQuestionFollows(lines, i + 1, choice.id))) {
      deductionTail = false;
      blocks.push({ type: 'section', text: line });
      const header = isQuestionLine(line);
      i += 1;
      if (header) {
        seenQuestion = true;
        i = consumeUntilNextQuestion(lines, i, blocks, paper, null);
      }
      continue;
    }

    if (isBareQuestionLabel(line)) {
      seenQuestion = true;
      deductionTail = false;
      let statement = '';
      i += 1;
      while (i < lines.length) {
        const extra = cleanLine(lines[i]);
        if (!extra) {
          i += 1;
          continue;
        }
        if (/^question\s*[:：]/i.test(extra)) {
          statement = extra.replace(/^question\s*[:：]\s*/i, '');
          i += 1;
        }
        break;
      }
      const question = parseQuestionLine(statement ? `${line} - ${statement}` : line);
      blocks.push(question);
      i = consumeUntilNextQuestion(lines, i, blocks, paper, question);
      continue;
    }

    if (isQuestionLine(line)) {
      seenQuestion = true;
      deductionTail = false;
      const question = parseQuestionLine(line);
      i += 1;
      while (i < lines.length) {
        const extra = cleanLine(lines[i]);
        if (!extra) {
          i += 1;
          continue;
        }
        if (isQuestionLine(extra) || isBareQuestionLabel(extra) || isBodyStart(extra) || isDataLine(extra)) break;
        question.rest = `${question.rest} ${extra}`.trim();
        question.text = `${question.text} ${extra}`.trim();
        const marks = question.text.match(/\(\s*([0-9]+(?:\.[0-9]+)?)\s*marks?\s*\)\s*$/i);
        if (marks) question.marks = marks[1];
        i += 1;
      }
      blocks.push(question);
      i = consumeUntilNextQuestion(lines, i, blocks, paper, question);
      continue;
    }

    if (!seenQuestion) {
      if (takeTotal(line, paper)) {
        deductionTail = false;
      } else if (isNoise(line) || isPreambleDeduction(line) || (deductionTail && isDeductionContinuation(line))) {
        deductionTail = true;
      } else {
        deductionTail = false;
        blocks.push({ type: 'section', text: line });
      }
    }
    i += 1;
  }

  hoistSharedPassages(blocks);
  blocks.forEach(attachPassageUnderlines);

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (block.type !== 'section') continue;
    const questions = [];
    let next = index + 1;
    while (next < blocks.length && blocks[next].type === 'question') {
      questions.push(blocks[next]);
      next += 1;
    }
    const parent = parseChoiceParent(block.text);
    if (parent) {
      const kids = questions.filter((question) => questionId(question.label).startsWith(`${parent.id}.`));
      const mark = parent.each || (kids[0] && kids[0].marks);
      if (kids.length && mark && !/\beach\b/i.test(block.text)) {
        block.text = block.text.replace(/\s*\(\s*[0-9]+(?:\.[0-9]+)?\s*marks?\s*\)\s*$/i, '').trim();
        const unit = Number(mark) === 1 ? 'mark' : 'marks';
        block.text = `${block.text} (each question carries ${mark} ${unit})`;
      }
      kids.forEach(stripQuestionMarks);
      continue;
    }
    const statesMarks = sectionStatesMarks(block.text);
    const saysEach = sectionSaysEach(block.text);
    const shared = sharedQuestionMarks(questions);
    if (statesMarks && !saysEach && shared) {
      block.text = `${block.text} — ${carriesPhrase(shared, block.text)}`;
    }
    if ((statesMarks && saysEach) || (statesMarks && shared)) {
      questions.forEach(stripQuestionMarks);
    }
  }

  const underlineMap = paperUnderlineMap(String(markdown || ''));
  blocks.forEach((block) => {
    if (block.type !== 'question') return;
    block.restParts = splitUnderlinedDigits(block.rest, underlineMap);
    block.textParts = splitUnderlinedDigits(block.text, underlineMap);
  });

  const sample = blocks.map((b) => (b.type === 'question' ? b.text : b.text)).join('\n');
  return {
    dir: scriptDir(sample),
    totalMarks: paper.totalMarks,
    blocks,
    questionCount: blocks.filter((b) => b.type === 'question').length
  };
}

export function attachRubricCard(paper, rubric) {
  if (!paper) return paper;
  const card = rubric || {};
  let dateLabel = '';
  if (card.test_date) {
    const parsed = new Date(card.test_date);
    if (!Number.isNaN(parsed.getTime())) {
      dateLabel = parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
  return {
    ...paper,
    className: String(card.class || '').trim(),
    subject: String(card.subject || '').trim(),
    dateLabel
  };
}

function paperHeading(paper) {
  const rtl = paper && paper.dir === 'rtl';
  const classLabel = rtl ? 'جماعت' : 'Class';
  const subjectLabel = rtl ? 'مضمون' : 'Subject';
  const dateLabel = rtl ? 'تاریخ' : 'Date';
  const left = [];
  if (paper.className) left.push(`${classLabel}: ${paper.className}`);
  if (paper.subject) left.push(`${subjectLabel}: ${paper.subject}`);
  const right = paper.dateLabel ? `${dateLabel}: ${paper.dateLabel}` : '';
  const total = paper.totalMarks ? `${rtl ? 'کل نمبر' : 'Total Marks'}: ${paper.totalMarks}` : '';
  return { left, right, total };
}

export function questionPaperToText(paper) {
  if (!paper || !paper.questionCount) return '';
  const rtl = paper.dir === 'rtl';
  const lines = [];
  const heading = paperHeading(paper);
  const headingLine = [...heading.left, heading.total, heading.right].filter(Boolean);
  if (headingLine.length) lines.push(headingLine.join('    '));
  lines.push('');
  lines.push(rtl ? 'رول نمبر:    نام:' : 'Roll Number:    Name:');
  lines.push('');
  for (const block of paper.blocks) {
    if (block.type === 'question') {
      lines.push(partsText(block.textParts, block.text));
      if (block.passageParts) lines.push(block.passageParts.map((line) => partsText(line, '')).join('\n'));
      else if (block.passage && block.passage.length) lines.push(block.passage.join('\n'));
    } else if (block.passageParts) {
      lines.push(block.passageParts.map((line) => partsText(line, '')).join('\n'));
    } else {
      lines.push(block.text);
    }
    lines.push('');
  }
  return `${lines.join('\n').trim()}\n`;
}

function partsText(parts, fallback) {
  if (!parts || !parts.length) return fallback || '';
  return parts.map((bit) => (bit.under ? `${bit.text}\u0332` : bit.text)).join('');
}

function passageLines(block) {
  if (block.passage && block.passage.length) return block.passage;
  if (block.type === 'passage') return String(block.text || '').split('\n');
  return [];
}

function tableHtml(segment) {
  const rows = segment.rows;
  const bodyRows = segment.header ? rows.slice(1) : rows;
  const head = segment.header
    ? `<thead><tr>${rows[0].map((cell) => `<th>${fractionHtml(cell)}</th>`).join('')}</tr></thead>`
    : '';
  const body = bodyRows.map((row) => (
    `<tr>${row.map((cell, index) => (
      index === 0
        ? `<th scope="row">${fractionHtml(cell)}</th>`
        : `<td>${fractionHtml(cell)}</td>`
    )).join('')}</tr>`
  )).join('');
  return `<table class="qp-data">${head}<tbody>${body}</tbody></table>`;
}

function passageHtml(block) {
  const lines = passageLines(block);
  if (!lines.length) return '';
  return segmentLines(lines).map((segment) => {
    if (segment.type === 'table') return tableHtml(segment);
    return segment.indexes.map((index) => {
      if (block.passageParts && block.passageParts[index]) return partsHtml(block.passageParts[index], '');
      return fractionHtml(lines[index]);
    }).join('<br>');
  }).join('');
}

function partsHtml(parts, fallback) {
  const bits = parts && parts.length ? parts : [{ text: fallback || '', under: false }];
  return bits.map((bit) => {
    const html = fractionHtml(bit.text);
    return bit.under ? `<u>${html}</u>` : html;
  }).join('');
}

function esc(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function questionPaperHtml(paper) {
  const rtl = paper && paper.dir === 'rtl';
  const heading = paperHeading(paper || {});
  const title = [...heading.left, heading.total, heading.right].filter(Boolean).join(' · ') || (rtl ? 'امتحان' : 'Exam');
  const meta = (heading.left.length || heading.total || heading.right)
    ? `<div class="meta"><div class="meta-side">${heading.left.map((item) => `<span>${esc(item)}</span>`).join('')}</div><div class="total">${esc(heading.total)}</div><div class="meta-side meta-side--end">${esc(heading.right)}</div></div>`
    : '';
  const rollLabel = rtl ? 'رول نمبر' : 'Roll Number';
  const nameLabel = rtl ? 'نام' : 'Name';
  const fields = `<span>${rollLabel}:</span><span>${nameLabel}:</span>`;
  const body = (paper && paper.blocks ? paper.blocks : []).map((block) => {
    if (block.type === 'section') {
      return `<h2 class="section">${esc(block.text)}</h2>`;
    }
    if (block.type === 'passage') {
      return `<div class="passage">${passageHtml(block)}</div>`;
    }
    const label = block.label ? `<span class="q-label">${esc(block.label)}</span> ${esc(block.sep)} ` : '';
    const passage = block.passage && block.passage.length
      ? `<div class="passage">${passageHtml(block)}</div>`
      : '';
    return `<p class="question">${label}${partsHtml(block.restParts, block.rest)}</p>${passage}`;
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="${rtl ? 'ur' : 'en'}" dir="${rtl ? 'rtl' : 'ltr'}">
<head>
<meta charset="utf-8">
<title>${title}</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 14mm 16mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #111; }
  .sheet {
    font-family: ${rtl
      ? '"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Segoe UI", sans-serif'
      : '"Segoe UI", "Noto Nastaliq Urdu", sans-serif'};
    font-size: 14pt;
  }
  .meta {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: baseline;
    gap: 18px;
    font-size: 14pt;
    font-weight: 700;
    margin: 0 0 8px;
  }
  .meta-side { display: flex; gap: 18px; flex-wrap: wrap; }
  .meta-side--end { justify-content: flex-end; }
  .total { text-align: center; font-weight: 700; white-space: nowrap; }
  .fields {
    display: flex;
    justify-content: flex-start;
    align-items: baseline;
    gap: 62px;
    margin: 0 0 16px;
    font-size: 14pt;
    font-weight: 700;
    line-height: 1.15;
  }
  .section { font-size: 15pt; font-weight: 700; margin: 16px 0 8px; }
  .question { margin: 0 0 12px; line-height: ${rtl ? '2.4' : '1.55'}; }
  .passage { margin: 0 0 14px; line-height: ${rtl ? '2.6' : '1.7'}; }
  .q-label { font-weight: 700; }
  u { text-decoration: underline; text-decoration-thickness: 2px; text-underline-offset: 3px; }
  .qp-frac { display: inline-flex; flex-direction: column; vertical-align: middle; text-align: center; margin: 0 0.15em; line-height: 1.05; font-size: 0.92em; }
  .qp-frac-num, .qp-frac-den { display: block; padding: 0 0.28em; }
  .qp-frac-num { border-bottom: 1.5px solid currentColor; }
  .qp-sup, .qp-sub { display: inline-block; font-size: 0.72em; line-height: 1.05; }
  .qp-sup { vertical-align: super; }
  .qp-sub { vertical-align: sub; }
  .qp-sqrt { display: inline-flex; align-items: stretch; vertical-align: middle; margin: 0 0.08em; line-height: 1; }
  .qp-sqrt-svg { width: 0.72em; height: auto; align-self: stretch; overflow: visible; flex: 0 0 auto; }
  .qp-sqrt-body { border-top: 1.5px solid currentColor; padding: 0.06em 0.2em 0.02em 0.08em; line-height: 1.15; }
  .qp-matrix { display: inline-flex; align-items: stretch; vertical-align: middle; margin: 0 0.12em; line-height: 1.2; }
  .qp-matrix-bracket { width: 0.32em; border-top: 1.5px solid currentColor; border-bottom: 1.5px solid currentColor; border-left: 1.5px solid currentColor; border-radius: 0.4em 0 0 0.4em; margin: 0.06em 0; }
  .qp-matrix-bracket--right { border-left: none; border-right: 1.5px solid currentColor; border-radius: 0 0.4em 0.4em 0; }
  .qp-matrix-rows { display: inline-flex; flex-direction: column; justify-content: center; padding: 0.08em 0.06em; }
  .qp-matrix-row { display: flex; align-items: center; justify-content: center; }
  .qp-matrix-cell { display: flex; align-items: center; justify-content: center; min-width: 0.6em; padding: 0.04em 0.38em; text-align: center; white-space: nowrap; }
  table.qp-data { width: auto; border-collapse: collapse; border: 1px solid #111; margin: 4px 0 14px; color: #111; font-size: 13pt; white-space: normal; line-height: 1.35; }
  table.qp-data th, table.qp-data td { border: 1px solid #111; padding: 3px 12px; text-align: center; font-weight: 600; color: #111; background: #fff; text-transform: none; letter-spacing: normal; white-space: nowrap; vertical-align: middle; }
  table.qp-data tbody tr:last-child th, table.qp-data tbody tr:last-child td { border-bottom: 1px solid #111; }
  table.qp-data th[scope="row"], table.qp-data thead th:first-child { text-align: left; background: #f8fafc; }
</style>
</head>
<body>
  <div class="sheet">
    ${meta}
    <div class="fields">${fields}</div>
    ${body}
  </div>
</body>
</html>`;
}

export function printQuestionPaper(paper) {
  if (!paper || !paper.questionCount || typeof document === 'undefined') return;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0';
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = win.document;
  doc.open();
  doc.write(questionPaperHtml(paper));
  doc.close();
  const cleanup = () => {
    try { iframe.remove(); } catch (_) { /* already gone */ }
  };
  const go = () => {
    win.focus();
    win.print();
    win.onafterprint = cleanup;
    setTimeout(cleanup, 1500);
  };
  if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(go).catch(go);
  else setTimeout(go, 300);
}
